/**
 * Pure helpers for finding a printing among all editions of a card.
 */

export interface PrintingLike {
  set: string;
  collector_number: string;
}

export interface PrintingSetInfo {
  name: string;
  releasedAt: string | null;
  iconSvgUri?: string;
}

export type SetInfoByCode = ReadonlyMap<string, PrintingSetInfo>;

const compareCollectorNumber = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

/** Newest set first; sets without a known release date go last (by set code, descending). */
export function sortPrintingsByRelease<T extends PrintingLike>(printings: readonly T[], setInfo: SetInfoByCode): T[] {
  return [...printings].sort((a, b) => {
    const releaseA = setInfo.get(a.set)?.releasedAt ?? null;
    const releaseB = setInfo.get(b.set)?.releasedAt ?? null;
    if (releaseA !== releaseB) {
      if (releaseA === null) return 1;
      if (releaseB === null) return -1;
      return releaseB.localeCompare(releaseA);
    }
    if (a.set !== b.set) return b.set.localeCompare(a.set);
    return compareCollectorNumber(a.collector_number, b.collector_number);
  });
}

/**
 * Filter printings by whitespace-separated tokens; every token must match the
 * set code (prefix), collector number (prefix, `#` optional) or set name (substring).
 */
export function filterPrintings<T extends PrintingLike>(printings: readonly T[], query: string, setInfo: SetInfoByCode): T[] {
  const tokens = query
    .toLowerCase()
    .split(/\s+/)
    .map((token) => token.replace(/^#/, ''))
    .filter(Boolean);
  if (tokens.length === 0) return [...printings];

  return printings.filter((printing) => {
    const setCode = printing.set.toLowerCase();
    const collectorNumber = printing.collector_number.toLowerCase();
    const setName = setInfo.get(printing.set)?.name.toLowerCase() ?? '';
    return tokens.every((token) => setCode.startsWith(token) || collectorNumber.startsWith(token) || setName.includes(token));
  });
}
